import React from 'react';
import Image, { ImageLoaderProps } from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';
import Icon from '../atoms/Icon';
import Property from '../atoms/Property';
import { byProperty } from '../../lib/utils';
import styles from '../../styles/components/molecules/ItemListed.module.scss';
import { Venue } from '../../graphql/types';
import { convertImageIDToURL } from '../../lib/apis/image';

interface ItemListedProps {
  className?: string;
  tiny?: boolean;
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  hero?: boolean;
  data: Partial<Venue>;
}

const normalizeSrc = (src: string) => {
  return src.startsWith('/') ? src.slice(1) : src;
};

const cloudflareLoader = ({ src, ...imageParams }: ImageLoaderProps) => {
  if (src.includes('https://imagedelivery.net') === false) {
    return src;
  }

  const supportedParams = ['width', 'height', 'quality'];
  const resizeParams = [];

  for (const [key, value] of Object.entries(imageParams)) {
    if (supportedParams.includes(key) && typeof value !== 'undefined') {
      resizeParams.push(`${key}=${value}`);
    }
  }

  const paramsString = resizeParams.join(',');
  const normalizedSrc = normalizeSrc(src);

  return `${normalizedSrc}/${paramsString}`;
};

export default function ItemListed(props: ItemListedProps) {
  const { itemListed } = styles;
  const { data: venue, className } = props;
  const {
    slug,
    cat,
    name,
    country,
    state,
    city,
    status,
    images,
    rating,
    rates,
    specs,
  } = venue as any;
  const itemType =
    status === 'promoted'
      ? 'large'
      : status === 'approved'
      ? 'medium'
      : 'small';
  const canShowVenueImage = ['approved', 'promoted'].includes(status || '');
  const thumbnail = canShowVenueImage && !!images?.length
    ? convertImageIDToURL(images[0].image_id)
    : null; // "/assets/thumbs/carousel_0001_venues_frankfurt.jpg";
  const locationStr = (
    status === 'promoted'
      ? `${country}, ${!!state ? state + ',' : ''} ${city}`
      : `${city}, ${country}`
  ).replace(/\b\w/g, (l) => l.toUpperCase());
  const statusStr =
    status === 'promoted'
      ? 'Promoted'
      : status === 'approved'
      ? 'Approved by owner'
      : '';
  const classes = clsx(itemListed, styles[itemType], className);
  const href = `/venue/${country}/${city}/${slug}`;

  return (
    <div className={classes}>
      <Link href={href}>
        <a>
          <figure>
            <figcaption>
              <p>
                <b>{name}</b>
                <br />
                {locationStr}
                {statusStr && <Property icon={status} label={statusStr} />}
              </p>
              <ul>
                <li>
                  {statusStr && <Property icon={status} label={statusStr} />}
                </li>
                {specs?.map((el: any, i: number) => (
                  <li key={i}>
                    <Property icon={el.id} value={el.value} unit={el.unit} />
                  </li>
                ))}
                <li>
                  <Property icon={'rating'} label="0" unit={`(${0})`} />
                </li>
              </ul>
            </figcaption>

            <div>
              {thumbnail ? (
                <Image
                  src={thumbnail}
                  alt={'an image'}
                  layout="fill"
                  objectFit="cover"
                  loader={cloudflareLoader}
                />
              ) : (
                <Icon name="venue" />
              )}
            </div>
          </figure>
        </a>
      </Link>
    </div>
  );
}
